/**
 * MegaMenu Block — Real Data
 * Fetches the Magento category tree and renders a full responsive mega-menu.
 *
 * Variants (data.content.variant):
 *   horizontal  — top-level inline, hover dropdowns (default)
 *   grouped     — dropdown grouped into clearly-headed columns
 *   with-image  — dropdown shows featured image alongside the link list
 *   stacked     — vertical sidebar nav (column of top-level), children inline
 *   slim-bar    — single-level only, no dropdowns at all
 *
 * Mobile is always a hamburger drawer regardless of variant.
 */

import React, { useEffect, useState, useRef } from 'react';
import { urlFor } from '@agentic/magento-client/lib/url-for';
import { createPortal } from 'react-dom';
import { getGraphQLHeaders, getGraphQLEndpoint } from '@agentic/magento-client/lib/storeConfig';

// Types

interface CategoryChild {
  id: number;
  uid: string;
  name: string;
  url_key: string;
  url_path: string;
  url_suffix?: string;
  include_in_menu: boolean;
  children_count: number;
  image?: string | null;
  children?: CategoryChild[];
}

interface CategoryTree {
  id: number;
  uid: string;
  name: string;
  url_key: string;
  children: CategoryChild[];
}

type MenuLayout = 'horizontal' | 'grouped' | 'with-image' | 'stacked' | 'slim-bar';

interface MegaMenuProps {
  data: {
    id?: string;
    rootCategoryId?: number;
    showSearch?: boolean;
    sticky?: boolean;
    variant?: string;
    content?: { variant?: string };
    featuredImage?: string;
    featuredImageLabel?: string;
    featuredImageHref?: string;
    [key: string]: unknown;
  };
  styles?: string;
  context?: unknown;
  variant?: string;
}

// GraphQL Query

const GET_CATEGORY_TREE = `
  query GetCategoryTree($id: Int!) {
    category(id: $id) {
      id uid name url_key
      children {
        id uid name url_key url_path include_in_menu children_count image
        children {
          id uid name url_key url_path include_in_menu children_count
          children {
            id uid name url_key url_path include_in_menu children_count
          }
        }
      }
    }
  }
`;

// Fallback nav (used when GraphQL fails)

const FALLBACK_NAV = [
  { name: 'Shop', url: '/category', children: [] },
  { name: 'New Arrivals', url: '/new-arrivals', children: [] },
  { name: 'Best Sellers', url: '/best-sellers', children: [] },
  { name: 'Sale', url: '/sale', children: [] },
  { name: 'Blog', url: '/blog', children: [] },
];

// Helpers

function buildCategoryUrl(cat: CategoryChild): string {
  const suffix = cat.url_suffix ?? '';
  // Prefer url_path so sub-categories with non-unique url_keys resolve
  // deterministically (e.g. "cable-avoidance/accessories" not just
  // "accessories" which collides under 4 different parents).
  return `${urlFor('category', cat.url_path || cat.url_key)}${suffix}`;
}

async function fetchCategoryTree(rootId: number, endpoint: string): Promise<CategoryTree | null> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: getGraphQLHeaders(),
    body: JSON.stringify({ query: GET_CATEGORY_TREE, variables: { id: rootId } }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  interface CategoryResp { errors?: Array<{ message?: string }>; data?: { category?: CategoryTree } }
  const json = await res.json() as CategoryResp;
  if (json.errors) throw new Error(json.errors[0]?.message ?? 'GraphQL error');
  return json.data?.category ?? null;
}

// Dropdown Panel

interface DropdownProps {
  category: CategoryChild;
  onClose: () => void;
  layout: MenuLayout;
  featuredImage?: string;
  featuredImageLabel?: string;
  featuredImageHref?: string;
  anchorEl: HTMLElement | null;
  onEnter: () => void;
  onLeave: () => void;
}

const DropdownPanel: React.FC<DropdownProps> = ({ category, onClose, layout, featuredImage, featuredImageLabel, featuredImageHref, anchorEl, onEnter, onLeave }) => {
  const children = (category.children ?? []).filter((c) => c.include_in_menu);

  const isGrouped = layout === 'grouped';
  const showImage = layout === 'with-image';
  const imageSrc = featuredImage || category.image || null;
  const minWidth = showImage ? 720 : 480;

  // Portal coords — anchor relative to LI bounding rect, position: fixed so the
  // portaled element ignores every parent stacking context.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!anchorEl) return;
    const updateCoords = () => {
      const rect = anchorEl.getBoundingClientRect();
      // top of dropdown sits just under the nav item, account for the 16px (mt-4) gap
      setCoords({ top: rect.bottom + 8, left: rect.left });
    };
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [anchorEl]);

  if (children.length === 0) return null;
  if (typeof document === 'undefined') return null;
  if (!coords) return null;

  const colCount = Math.min(4, Math.ceil(children.length / 4));
  const columns: CategoryChild[][] = Array.from({ length: colCount }, () => []);
  children.forEach((child, i) => columns[i % colCount].push(child));

  const panel = (
    <div
      className="w-max mega-menu-dropdown"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 99999,
        borderRadius: 'var(--style-card-radius, 12px)',
        backgroundColor: 'var(--color-header-dropdown-bg, var(--style-modal-bg, var(--surface-1, #0c0e14)))',
        border: '1px solid var(--glass-border, rgba(255,255,255,0.1))',
        boxShadow: 'var(--style-elevated-shadow, 0 20px 40px rgba(0,0,0,0.3))',
        backdropFilter: 'var(--style-backdrop-filter, blur(16px))',
        WebkitBackdropFilter: 'var(--style-backdrop-filter, blur(16px))',
        minWidth,
        maxWidth: 960,
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="p-8 flex gap-8">
        <div
          className="grid gap-8 flex-1"
          style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}
        >
          {columns.map((col, ci) => (
            <div key={ci}>
              {col.map((sub) => (
                <div key={sub.uid} className="mb-4">
                  <a
                    href={buildCategoryUrl(sub)}
                    className={`block font-semibold hover:text-[var(--color-header-dropdown-text-hover,var(--accent-primary))] transition-colors mb-1.5 ${isGrouped ? 'uppercase tracking-widest' : ''}`}
                    style={{
                      color: 'var(--color-header-dropdown-text, var(--text-primary, #f1f5f9))',
                      fontSize: isGrouped ? '11px' : 'var(--style-nav-dropdown-heading-font-size, 15px)',
                      letterSpacing: isGrouped ? '0.12em' : undefined,
                      borderBottom: isGrouped ? '1px solid color-mix(in srgb, var(--glass-border) 60%, transparent)' : undefined,
                      paddingBottom: isGrouped ? '6px' : undefined,
                    }}
                    onClick={onClose}
                  >
                    {sub.name}
                  </a>
                  {sub.children && sub.children.length > 0 && (
                    <ul className="space-y-1">
                      {sub.children
                        .filter((c) => c.include_in_menu)
                        .slice(0, 6)
                        .map((leaf) => (
                          <li key={leaf.uid}>
                            <a
                              href={buildCategoryUrl(leaf)}
                              className="block hover:text-[var(--color-header-dropdown-text-hover,var(--accent-primary))] transition-colors py-1"
                              style={{
                                color: 'var(--color-header-dropdown-text, var(--text-secondary, #cbd5e1))',
                                fontSize: 'var(--style-nav-dropdown-font-size, 14px)',
                              }}
                              onClick={onClose}
                            >
                              {leaf.name}
                            </a>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {showImage && imageSrc && (
          <a
            href={featuredImageHref || buildCategoryUrl(category)}
            className="block w-64 flex-shrink-0 relative overflow-hidden group"
            style={{
              borderRadius: 'var(--style-card-radius, 10px)',
              border: '1px solid var(--glass-border)',
              minHeight: 200,
            }}
            onClick={onClose}
          >
            <img
              src={imageSrc}
              alt={featuredImageLabel || category.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:[transform:var(--style-img-hover-transform,scale(1.05))]"
              style={{ transitionDuration: 'var(--style-transition-duration, 400ms)', transitionTimingFunction: 'var(--style-transition-easing, cubic-bezier(.2,.8,.2,1))' }}
              loading="lazy"
            />
            <div
              className="absolute inset-x-0 bottom-0 p-3"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)' }}
            >
              <span
                className="block text-sm font-bold"
                style={{ color: '#fff' }}
              >
                {featuredImageLabel || `Shop ${category.name} →`}
              </span>
            </div>
          </a>
        )}
      </div>

      <div
        className="px-6 py-3 rounded-b-xl"
        style={{
          borderTop: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
          backgroundColor: 'var(--color-header-dropdown-bg, var(--surface-2, #1e293b))',
        }}
      >
        <a
          href={buildCategoryUrl(category)}
          /* 2026-05-22 — was text-[var(--color-accent-fg)] which for any
             preset whose primary clashes with the dropdown bg goes
             invisible (Industrial Hardware Dark: #D13200 safety orange
             on #1E1E1E dropdown = 2.14:1 FAIL). Use chrome-scoped
             --color-header-dropdown-text-hover — each preset defines
             this per its own dropdown bg, so it always contrasts. */
          className="text-xs font-medium text-[var(--color-header-dropdown-text-hover,var(--color-header-dropdown-text,var(--text-primary)))] hover:underline"
          onClick={onClose}
        >
          View all in {category.name} →
        </a>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

// Nav Item (horizontal/grouped/with-image)

interface NavItemProps {
  category: CategoryChild;
  layout: MenuLayout;
  featuredImage?: string;
  featuredImageLabel?: string;
  featuredImageHref?: string;
}

const NavItem: React.FC<NavItemProps> = ({ category, layout, featuredImage, featuredImageLabel, featuredImageHref }) => {
  const [open, setOpen] = useState(false);
  const liRef = useRef<HTMLLIElement | null>(null);
  // Keep anchorEl in state so the dropdown re-computes coords once the LI mounts.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  useEffect(() => { setAnchorEl(liRef.current); }, []);

  const childCount = (category.children ?? []).filter((c) => c.include_in_menu).length;
  const hasChildren = childCount > 0;
  const showChevron = hasChildren && layout !== 'slim-bar';
  const enableDropdown = hasChildren && layout !== 'slim-bar';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (!enableDropdown) return;
    cancelClose();
    timerRef.current = setTimeout(() => setOpen(false), 150);
  };

  const handleMouseEnter = () => {
    if (!enableDropdown) return;
    cancelClose();
    setOpen(true);
  };

  return (
    <li
      ref={liRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={scheduleClose}
    >
      <a
        href={buildCategoryUrl(category)}
        className={`mega-nav-link flex items-center gap-1.5 px-4 py-2.5 transition-all whitespace-nowrap relative ${open ? 'is-open' : ''}`}
        style={{
          color: 'var(--color-header-menu-text, currentColor)',
          fontSize: 'var(--style-nav-font-size, 14px)',
          fontWeight: 'var(--style-nav-font-weight, 500)' as React.CSSProperties['fontWeight'],
          letterSpacing: 'var(--style-nav-letter-spacing, 0.01em)',
          textTransform: 'var(--style-nav-transform, none)' as React.CSSProperties['textTransform'],
        }}
      >
        {category.name}
        {showChevron && (
          <svg
            className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, currentColor)"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </a>

      {/* CRAWLABLE SUB-CATEGORY LINKS — do not remove.
          The interactive DropdownPanel below is a PORTAL that bails out with
          `typeof document === 'undefined' → null` on the server and needs
          `coords` from a useEffect, and it only mounts once `open` flips true on
          mouseenter. Crawlers never hover, so until this list existed NO
          sub-category link appeared anywhere in the delivered HTML — the whole
          middle tier of the catalogue (Batteries, Antennas, TETRA, Licenced
          Radios…) had zero internal inlinks despite being in the sitemap.
          This is a plain <ul>: no portal, no fixed positioning, no
          backdrop-filter, no scroll/resize listeners — so it costs nothing at
          runtime. Always-mounting the real panel instead would have added one
          blurred fixed layer AND one capturing scroll listener per top-level
          category, which is a measurable scroll-perf regression.
          Derived purely from the `category` prop, so SSR and hydration match. */}
      {hasChildren && (
        <ul style={{ display: 'none' }} aria-hidden="true">
          {(category.children ?? [])
            .filter((c) => c.include_in_menu)
            .map((sub) => (
              <li key={sub.id ?? sub.uid}>
                <a href={buildCategoryUrl(sub)}>{sub.name}</a>
              </li>
            ))}
        </ul>
      )}

      {enableDropdown && open && (
        <DropdownPanel
          category={category}
          onClose={() => setOpen(false)}
          layout={layout}
          featuredImage={featuredImage}
          featuredImageLabel={featuredImageLabel}
          featuredImageHref={featuredImageHref}
          anchorEl={anchorEl}
          onEnter={cancelClose}
          onLeave={scheduleClose}
        />
      )}
    </li>
  );
};

// Stacked Nav Item (vertical sidebar)

const StackedNavItem: React.FC<{ category: CategoryChild }> = ({ category }) => {
  const [open, setOpen] = useState(false);
  const children = (category.children ?? []).filter((c) => c.include_in_menu);
  const hasChildren = children.length > 0;

  return (
    <li>
      <div className="flex items-center">
        <a
          href={buildCategoryUrl(category)}
          className="flex-1 px-3 py-2 text-sm font-medium transition-colors hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,transparent)]"
          style={{
            color: 'var(--color-header-menu-text, var(--text-primary))',
            borderRadius: 'var(--style-btn-radius, 6px)',
          }}
        >
          {category.name}
        </a>
        {hasChildren && (
          <button
            onClick={() => setOpen(o => !o)}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${category.name}`}
            aria-expanded={open}
            className="p-2 text-[var(--text-muted)]"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <svg
              className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
      {hasChildren && open && (
        <ul className="ml-3 mt-1 mb-2 space-y-0.5 border-l pl-3" style={{ borderColor: 'var(--glass-border)' }}>
          {children.map(c => (
            <li key={c.uid}>
              <a
                href={buildCategoryUrl(c)}
                className="block py-1.5 text-xs transition-colors hover:text-[var(--color-global-link-hover)]"
                style={{ color: 'var(--text-secondary)' }}
              >
                {c.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

// Mobile Menu (drawer)

const MobileMenu: React.FC<{
  categories: CategoryChild[];
  open: boolean;
  onClose: () => void;
  isHamburgerDesktop?: boolean;
}> = ({ categories, open, onClose, isHamburgerDesktop }) => {
  const [expanded, setExpanded] = useState<number | null>(null);
  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 ${isHamburgerDesktop ? '' : 'lg:hidden'}`}>
      <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--surface-1)_60%,transparent)] backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-80 bg-[var(--surface-1,#0f172a)] border-r border-[var(--glass-border,rgba(255,255,255,0.08))] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[var(--glass-border,rgba(255,255,255,0.08))]">
          <span className="font-bold text-[var(--text-primary,#f1f5f9)]">Menu</span>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="p-2 hover:bg-[var(--surface-2,#1e293b)] text-[var(--text-muted,#64748b)]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, currentColor)">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {categories.map((cat) => {
            const children = (cat.children ?? []).filter((c) => c.include_in_menu);
            const isExpanded = expanded === cat.id;
            return (
              <div key={cat.uid}>
                <div className="flex items-center">
                  <a
                    href={buildCategoryUrl(cat)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-[var(--text-primary,#f1f5f9)] hover:text-[var(--color-global-link-hover)] transition-colors"
                    onClick={onClose}
                  >
                    {cat.name}
                  </a>
                  {children.length > 0 && (
                    <button
                      onClick={() => setExpanded(isExpanded ? null : cat.id)}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${cat.name}`}
                      className="p-2 text-[var(--text-muted,#64748b)]"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, currentColor)"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                {isExpanded && children.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-[var(--glass-border,rgba(255,255,255,0.08))] pl-3">
                    {children.map((sub) => (
                      <a
                        key={sub.uid}
                        href={buildCategoryUrl(sub)}
                        className="block py-1.5 text-sm text-[var(--text-secondary,#cbd5e1)] hover:text-[var(--color-global-link-hover)] transition-colors"
                        onClick={onClose}
                      >
                        {sub.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

// Main Component

export const MegaMenu: React.FC<MegaMenuProps> = ({ data, styles, context, variant }) => {
  const { rootCategoryId = 2 } = data ?? {};
  const isHamburgerDesktop = variant === 'hamburger' || data?.variant === 'hamburger' || data?.hamburgerMode === true;

  // Resolve layout variant
  const variantRaw = data?.content?.variant
    ?? (typeof data?.variant === 'string' ? data.variant : undefined)
    ?? (typeof variant === 'string' ? variant : undefined)
    ?? 'horizontal';
  const layoutVariant: MenuLayout =
    (['horizontal', 'grouped', 'with-image', 'stacked', 'slim-bar'] as const).includes(variantRaw as MenuLayout)
      ? variantRaw as MenuLayout
      : 'horizontal';

  // Initial categories — PROP ONLY, so the first render is deterministic across
  // SSR and client hydration. We deliberately do NOT read window.__SSR_CONTEXT__
  // here: `window` is undefined on the server, so reading it during render makes
  // the first CLIENT render differ from the SSR HTML (server renders empty, client
  // renders the full nav) → React #418 hydration mismatch. The window-context
  // fast path is applied AFTER mount, in the effect below.
  const __getInitialCat = (): CategoryChild[] => {
    const fromProp = (context as { categoryTree?: { children?: CategoryChild[] } } | undefined)?.categoryTree?.children;
    if (Array.isArray(fromProp) && fromProp.length > 0) {
      return fromProp.filter((c: CategoryChild) => c?.include_in_menu);
    }
    return [];
  };
  const initialCat = __getInitialCat();

  const [categories, setCategories] = useState<CategoryChild[]>(initialCat);
  const [loading, setLoading] = useState(initialCat.length === 0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const endpoint = getGraphQLEndpoint();

  useEffect(() => {
    if (initialCat.length > 0) return;
    // Post-mount fast path: reuse the SSR-serialized category tree from
    // window.__SSR_CONTEXT__ if present (skips the GraphQL round-trip). Reading
    // it HERE — after hydration — instead of during render keeps the first
    // render deterministic, so there's no #418 mismatch.
    try {
      const fromWin = (window as Window & { __SSR_CONTEXT__?: { categoryTree?: { children?: CategoryChild[] } } }).__SSR_CONTEXT__?.categoryTree?.children;
      if (Array.isArray(fromWin) && fromWin.length > 0) {
        const visible = fromWin.filter((c: CategoryChild) => c?.include_in_menu);
        if (visible.length > 0) {
          setCategories(visible);
          setLoading(false);
          return;
        }
      }
    } catch { /* ignore — fall through to the GraphQL fetch */ }
    let cancelled = false;
    fetchCategoryTree(rootCategoryId, endpoint)
      .then((tree) => {
        if (!cancelled && tree) {
          const visible = (tree.children ?? []).filter((c) => c.include_in_menu);
          setCategories(visible);
        }
      })
      .catch((err) => {
        console.warn('[MegaMenu] GraphQL failed, using fallback nav:', err);
        if (!cancelled) {
          setCategories(
            FALLBACK_NAV.map((item, i) => ({
              id: i,
              uid: `fallback-${i}`,
              name: item.name,
              url_key: item.url.replace('/', ''),
              url_path: item.url.replace('/', ''),
              include_in_menu: true,
              children_count: 0,
              children: [],
            }))
          );
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [rootCategoryId, endpoint, initialCat.length]);

  // Variant: stacked — vertical sidebar nav (desktop)
  if (layoutVariant === 'stacked' && !isHamburgerDesktop) {
    return (
      <nav
        className={`relative w-full ${styles ?? ''}`}
        aria-label="Main navigation"
        data-variant="stacked"
      >
        {loading ? (
          <div className="hidden lg:flex flex-col gap-2 p-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-8 w-full bg-[var(--surface-2)] animate-pulse" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }} />
            ))}
          </div>
        ) : (
          <ul className="hidden lg:flex flex-col gap-0.5 p-2">
            {categories.map(c => (
              <StackedNavItem key={c.uid} category={c} />
            ))}
          </ul>
        )}

        {/* Mobile hamburger — radius tracks the active style preset's --style-btn-radius. */}
        <button
          className="lg:hidden flex items-center p-2 text-[var(--text-secondary,#cbd5e1)] hover:bg-[var(--white-5,rgba(255,255,255,0.05))] transition-colors"
          style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, var(--text-primary,#fff))">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <MobileMenu categories={categories} open={mobileOpen} onClose={() => setMobileOpen(false)} />
      </nav>
    );
  }

  // Default / horizontal / grouped / with-image / slim-bar
  return (
    <nav
      className={`relative w-full ${styles ?? ''}`}
      aria-label="Main navigation"
      data-variant={layoutVariant}
    >
      <div className="w-full flex items-center justify-center">
        <div className="flex items-center">
          {!isHamburgerDesktop && (
            loading ? (
              <div className="hidden lg:flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-8 w-20 bg-[var(--surface-2,#1e293b)] animate-pulse" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }} />
                ))}
              </div>
            ) : (
              <ul className={`hidden lg:flex items-center gap-1 flex-1 ${layoutVariant === 'slim-bar' ? 'mega-slim-bar' : ''}`}>
                {categories.map((cat) => (
                  <NavItem
                    key={cat.uid}
                    category={cat}
                    layout={layoutVariant}
                    featuredImage={typeof data?.featuredImage === 'string' ? data.featuredImage : undefined}
                    featuredImageLabel={typeof data?.featuredImageLabel === 'string' ? data.featuredImageLabel : undefined}
                    featuredImageHref={typeof data?.featuredImageHref === 'string' ? data.featuredImageHref : undefined}
                  />
                ))}
              </ul>
            )
          )}

          <button
            className={`${isHamburgerDesktop ? 'flex' : 'lg:hidden'} items-center p-2 text-[var(--text-secondary,#cbd5e1)] hover:bg-[var(--white-5,rgba(255,255,255,0.05))] transition-colors`}
            style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, var(--text-primary,#fff))">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            {isHamburgerDesktop && <span className="ml-2 text-sm font-bold tracking-wider text-[var(--text-primary,#fff)] uppercase" style={{ color: 'var(--icon-color, #fff)' }}>Menu</span>}
          </button>
        </div>
      </div>

      <MobileMenu
        categories={categories}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        isHamburgerDesktop={isHamburgerDesktop}
      />
    </nav>
  );
};

export default MegaMenu;
